import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, Stack, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, SearchField } from '../../components/data-grid/RouteDataGrid';
import { EntityDetailDialog } from '../../components/dialogs/EntityDetailDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { fmtDateTime } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import {
  useUserAuditHistory, useUserDetail, type UserAuditEvent,
} from '../../api/queries';

/**
 * /users/:userId/login-audit — unified per-user audit timeline.
 *
 * Combines `auth.login_audit` (sign-in attempts, TOTP setup, password
 * change attempts) and `audit.audit_log` / `audit.platform_audit_log`
 * (every operator / platform state change attributable to this user)
 * into a single descending timeline. Ops investigating "did Bob log in
 * AND what did he change while logged in?" can now read it in one place.
 *
 * RLS already scopes operator-tenant callers to their own operator's
 * rows; ARMS callers see the lot.
 */
export default function UserLoginAuditPage() {
  usePageTitle('User audit history');
  const { userId = '' } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const user = useUserDetail(userId);
  const list = useUserAuditHistory(userId);
  const [selected, setSelected] = useState<UserAuditEvent | null>(null);

  const subjectLabel = user.data?.displayName || user.data?.email || userId;

  const columns = useMemo<GridColDef<UserAuditEvent>[]>(() => [
    { field: 'occurredAt', headerName: 'When', width: 190,
      renderCell: (p) => (
        <Typography variant="body2">{fmtDateTime(p.row.occurredAt)}</Typography>
      ) },
    { field: 'kind', headerName: 'Kind', width: 110,
      renderCell: (p) => <KindChip kind={p.row.kind} /> },
    { field: 'eventType', headerName: 'Event', width: 240,
      renderCell: (p) => <EventChip kind={p.row.kind} event={p.row.eventType} /> },
    { field: 'outcome', headerName: 'Outcome', width: 110,
      renderCell: (p) => <OutcomeChip outcome={p.row.outcome} /> },
    { field: 'context', headerName: 'Context', flex: 1.2, sortable: false,
      renderCell: (p) => <ContextCell row={p.row} /> },
    { field: 'reasonOrDetail', headerName: 'Reason / detail', flex: 1, sortable: false,
      renderCell: (p) => {
        const text = p.row.kind === 'login' ? p.row.outcomeDetail : p.row.reason;
        if (!text) {
          return <Typography variant="caption" color="text.disabled">—</Typography>;
        }
        return (
          <Tooltip title={text}>
            <Typography variant="caption" sx={{
              fontFamily: 'monospace', fontSize: 11,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {text}
            </Typography>
          </Tooltip>
        );
      },
    },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text', key: 'eventType', label: 'Event type' },
    { kind: 'text', key: 'ipAddress', label: 'IP address' },
    { kind: 'text', key: 'subjectResourceType', label: 'Subject type' },
  ], []);

  const quickFilters = useMemo<QuickFilter<UserAuditEvent>[]>(() => [
    { label: 'All',            filter: () => true },
    { label: 'Sign-ins',       filter: (r) => r.kind === 'login' },
    { label: 'Activity',       filter: (r) => r.kind === 'activity' },
    { label: 'Failures',       filter: (r) => r.outcome === 'failure' },
    { label: 'Two-factor',     filter: (r) => r.factorUsed === 'totp' || r.eventType.startsWith('totp.') },
    { label: 'Password',       filter: (r) => r.factorUsed === 'password' || r.eventType.startsWith('password.') },
  ], []);

  const exportColumns = useMemo<ExportColumn<UserAuditEvent>[]>(() => [
    { key: 'occurredAt',          label: 'When', format: (v) => fmtDateTime(v) },
    { key: 'kind',                label: 'Kind' },
    { key: 'eventType',           label: 'Event' },
    { key: 'outcome',             label: 'Outcome' },
    { key: 'factorUsed',          label: 'Factor' },
    { key: 'ipAddress',           label: 'IP' },
    { key: 'geoipCountry',        label: 'Country' },
    { key: 'subjectResourceType', label: 'Subject type' },
    { key: 'subjectDisplayName',  label: 'Subject' },
    { key: 'reason',              label: 'Reason' },
    { key: 'outcomeDetail',       label: 'Sign-in detail' },
    { key: 'userAgent',           label: 'User agent' },
    { key: 'correlationId',       label: 'Correlation' },
  ], []);

  const detailFields = useMemo<DetailRow<UserAuditEvent>[]>(() => [
    { label: 'When',           render: (r) => fmtDateTime(r.occurredAt) },
    { label: 'Kind',           render: (r) => <KindChip kind={r.kind} /> },
    { label: 'Event',          render: (r) => <EventChip kind={r.kind} event={r.eventType} /> },
    { label: 'Outcome',        render: (r) => <OutcomeChip outcome={r.outcome} /> },

    // Login-specific block
    { label: 'Factor used',    separator: true,
      render: (r) => r.kind === 'login' ? (r.factorUsed ?? '—') : <em style={{ color: '#999' }}>not applicable</em> },
    { label: 'IP address',
      render: (r) => r.kind === 'login' && r.ipAddress
        ? <Typography sx={{ fontFamily: 'monospace' }}>{r.ipAddress}</Typography>
        : (r.kind === 'login' ? '—' : <em style={{ color: '#999' }}>not applicable</em>) },
    { label: 'Country',
      render: (r) => r.kind === 'login' ? (r.geoipCountry ?? '—') : <em style={{ color: '#999' }}>not applicable</em> },
    { label: 'User agent',
      render: (r) => r.kind === 'login' && r.userAgent
        ? <Typography sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{r.userAgent}</Typography>
        : (r.kind === 'login' ? '—' : <em style={{ color: '#999' }}>not applicable</em>) },
    { label: 'Sign-in detail',
      render: (r) => r.kind === 'login' && r.outcomeDetail
        ? <Typography sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{r.outcomeDetail}</Typography>
        : (r.kind === 'login' ? '—' : <em style={{ color: '#999' }}>not applicable</em>) },

    // Activity-specific block
    { label: 'Tier',           separator: true,
      render: (r) => r.kind === 'activity'
        ? <Chip size="small" label={r.tier ?? '—'} color={r.tier === 'platform' ? 'secondary' : 'default'} />
        : <em style={{ color: '#999' }}>not applicable</em> },
    { label: 'Subject',
      render: (r) => {
        if (r.kind !== 'activity') return <em style={{ color: '#999' }}>not applicable</em>;
        if (!r.subjectResourceType) return '—';
        const friendly = r.subjectResourceType.replace(/_/g, ' ');
        return r.subjectDisplayName
          ? `${r.subjectDisplayName} (${friendly})`
          : friendly;
      } },
    { label: 'Subject type',
      render: (r) => r.kind === 'activity' ? (r.subjectResourceType ?? '—') : <em style={{ color: '#999' }}>not applicable</em> },
    { label: 'Reason',
      render: (r) => r.kind === 'activity' ? (r.reason ?? '—') : <em style={{ color: '#999' }}>not applicable</em> },

    { label: 'Correlation ID', separator: true,
      render: (r) => (
        <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.correlationId}</Typography>
      ) },
  ], []);

  return (
    <>
      <Stack spacing={2}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/users')}
            sx={{ mb: 1 }}
            size="small"
          >
            Back to users
          </Button>
          <Typography variant="h4">Audit history — {subjectLabel}</Typography>
          <Typography variant="body2" color="text.secondary">
            Sign-in events and every state change attributable to this user, newest first.
            Rows are immutable; corrections happen by adding compensating entries.
          </Typography>
        </Box>

        <RouteDataGrid<UserAuditEvent>
          entityName="Audit events"
          queryKey={list.queryKey}
          listFn={list.listFn}
          columns={columns}
          searchFields={searchFields}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle={`ARMS — Audit history · ${subjectLabel}`}
          emptyMessage="No sign-in or activity recorded for this user yet."
          onRowClick={(row) => setSelected(row)}
        />

        <EntityDetailDialog<UserAuditEvent>
          open={selected !== null}
          title={selected ? `${selected.kind === 'login' ? 'Sign-in event' : 'Activity'} · ${selected.eventType}` : 'Audit event'}
          data={selected}
          fields={detailFields}
          onClose={() => setSelected(null)}
          maxWidth="md"
        />
      </Stack>
    </>
  );
}

function KindChip({ kind }: { kind: 'login' | 'activity' }) {
  return (
    <Chip
      size="small"
      label={kind === 'login' ? 'Sign-in' : 'Activity'}
      color={kind === 'login' ? 'primary' : 'default'}
      variant="outlined"
    />
  );
}

function EventChip({ kind, event }: { kind: 'login' | 'activity'; event: string }) {
  const color: 'success' | 'error' | 'info' | 'warning' | 'default' =
    kind === 'login' ? (
      event === 'login.success' || event === 'login.password_ok' ? 'success' :
      event === 'login.failure' || event === 'totp.failed' || event === 'totp.setup_failed' || event === 'password.change_failed' ? 'error' :
      event.startsWith('totp.') ? 'info' :
      event.startsWith('password.') ? 'warning' : 'default'
    ) : 'default';
  return (
    <Chip
      size="small"
      label={event}
      color={color}
      sx={{ fontFamily: 'monospace', fontSize: 11 }}
    />
  );
}

function OutcomeChip({ outcome }: { outcome: string }) {
  const color: 'success' | 'error' | 'default' =
    outcome === 'success' ? 'success' :
    outcome === 'failure' ? 'error' : 'default';
  return <Chip size="small" label={outcome} color={color} />;
}

function ContextCell({ row }: { row: UserAuditEvent }) {
  if (row.kind === 'login') {
    const parts: string[] = [];
    if (row.factorUsed) parts.push(row.factorUsed);
    if (row.ipAddress) parts.push(row.ipAddress);
    if (row.geoipCountry) parts.push(row.geoipCountry);
    if (parts.length === 0) {
      return <Typography variant="caption" color="text.disabled">—</Typography>;
    }
    return (
      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
        {parts.join(' · ')}
      </Typography>
    );
  }
  // activity
  if (!row.subjectResourceType) {
    return <Typography variant="caption" color="text.disabled">—</Typography>;
  }
  const friendly = row.subjectResourceType.replace(/_/g, ' ');
  const label = row.subjectDisplayName
    ? `${row.subjectDisplayName} (${friendly})`
    : friendly;
  return <Typography variant="body2">{label}</Typography>;
}
